import { useRef } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { AccountingEngine } from '../services/accountingEngine';

export const GroupSummaryView = ({ config, activeMenuLevel, handleItemSelect, selectedIndex, setSelectedIndex }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  
  const groupData = useLiveQuery(async () => {
    if (!activeCompany) return [];
    
    const ledgers = await db.ledgers.where('companyId').equals(activeCompany.id!).toArray();
    const items = [];
    
    for (const l of ledgers) {
      const bal = await AccountingEngine.getLedgerBalance(l.id!, undefined, toDate);
      items.push({
        id: l.id,
        name: l.name,
        debit: bal.type === 'Dr' && bal.balance > 0 ? bal.balance.toFixed(2) : '',
        credit: bal.type === 'Cr' && bal.balance > 0 ? bal.balance.toFixed(2) : ''
      });
    }
    return items;
  }, [activeCompany, fromDate, toDate, activeMenuLevel]);

  const flatItems = groupData || [];
  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => flatItems && flatItems[selectedIndex] && handleItemSelect(flatItems[selectedIndex], selectedIndex));

  const totalDr = flatItems.reduce((sum, item) => sum + (Number(item.debit) || 0), 0);
  const totalCr = flatItems.reduce((sum, item) => sum + (Number(item.credit) || 0), 0);

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Group Summary</span>
        <span className="w-1/3 text-center">{activeCompany?.name || config.companyList[0].name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex border-b border-gray-300 px-2 py-1 font-bold items-end text-black shadow-sm z-10">
        <div className="flex-1 pb-[2px]">Particulars</div>
        <div className="w-[200px] flex flex-col items-end text-[11px] leading-tight border-l border-gray-300 pl-2">
           <span className="italic font-normal text-[12px] text-gray-800 mb-[1px]">All Ledgers</span>
           <span>{activeCompany?.name}</span>
           <span className="font-normal mb-[2px]">{fromDate} to {toDate}</span>
           <span className="border-b border-gray-300 w-full text-center pb-[2px]">Closing Balance</span>
           <div className="flex w-full justify-between pt-[2px]">
              <span className="w-1/2 text-right pr-2">Debit</span>
              <span className="w-1/2 text-right">Credit</span>
           </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-1 py-1 scrollbar-thin">
         {flatItems.map((item: any, idx: number) => {
            const isSelected = selectedIndex === idx;
            return (
               <div key={idx} onClick={() => handleItemSelect(item, idx)} className={`flex px-1 py-[2px] cursor-pointer ${isSelected ? 'bg-[#1e8585] text-white font-bold active-row' : 'font-bold text-black'}`}>
                  <span className="flex-1">{item.name}</span>
                  <div className="w-[200px] flex">
                      <span className="w-1/2 text-right pr-2">{item.debit}</span>
                      <span className="w-1/2 text-right">{item.credit}</span>
                  </div>
               </div>
            );
         })}
      </div>

      <div className="flex border-t border-gray-400 px-2 py-1 font-bold text-black z-10">
        <span className="flex-1">Grand Total</span>
        <div className="w-[200px] flex">
           <span className="w-1/2 text-right pr-2">{totalDr > 0 ? totalDr.toFixed(2) : ''}</span>
           <span className="w-1/2 text-right">{totalCr > 0 ? totalCr.toFixed(2) : ''}</span>
        </div>
      </div>
    </div>
  );
};
